import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  bulkUpsertSettings,
  listSettings,
  type BulkSettingsEntry,
  type PlatformSetting,
} from '@/lib/operatorApi';
import {
  looksMasked,
  type FieldDef,
  type SettingValue,
} from '@/data/settings';

// ────────────────────────────────────────────────────────────────────────
// useSettings — единый источник состояния для страницы «Настройки».
//
// Центральная точка загружает все настройки один раз, держит их в Map,
// отдаёт секциям через `get(key)` / `ensure(fields)` и предоставляет
// `saveSection(section, entries)` для массовой записи. Секции сами решают,
// какие поля считать грязными: например, секретные значения отправляются
// только если пользователь реально что-то ввёл в поле.
// ────────────────────────────────────────────────────────────────────────

export interface SettingsState {
  /** true, пока идёт первичная загрузка. */
  loading: boolean;
  /** Сообщение об ошибке загрузки, если есть. */
  error: string | null;
  /** Текущая версия — увеличивается после каждой записи или reload. */
  version: number;
  /** Сырые значения из БД, ключ → value (уже замаскированный для секретов). */
  raw: Map<string, unknown>;
  /** Метаданные по ключам (когда и кем обновлён). */
  meta: Map<string, { updatedAt: string; updatedBy: string | null; isSecret: boolean }>;

  /** Достать значение с фолбеком на default. */
  get<T extends SettingValue>(field: FieldDef): T;
  /** Проверить, есть ли сохранённое значение (не default) в БД. */
  has(field: FieldDef): boolean;
  /** Проверить, содержит ли поле замаскированный секрет. */
  isMasked(field: FieldDef): boolean;

  /** Перезагрузить состояние с сервера. */
  reload: () => Promise<void>;
  /** Сохранить пачку записей одной секции. Возвращает true, если ок. */
  saveSection: (section: string, entries: BulkSettingsEntry[]) => Promise<SaveResult>;
}

export interface SaveResult {
  ok: boolean;
  error?: string;
  /** Ключи, которые реально были записаны (секреты-маски пропускаются). */
  writtenKeys: string[];
}

export function useSettings(): SettingsState {
  const [rows, setRows] = useState<PlatformSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSettings();
      setRows(res.results);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { raw, meta } = useMemo(() => {
    const raw = new Map<string, unknown>();
    const meta = new Map<string, { updatedAt: string; updatedBy: string | null; isSecret: boolean }>();
    for (const r of rows) {
      raw.set(r.key, r.value);
      meta.set(r.key, { updatedAt: r.updatedAt, updatedBy: r.updatedBy, isSecret: r.isSecret });
    }
    return { raw, meta };
  }, [rows]);

  const get = useCallback(
    <T extends SettingValue>(field: FieldDef): T => {
      if (!raw.has(field.key)) return field.default as T;
      const v = raw.get(field.key);
      // Секрет читаем как строку-маску — её отдельно переспрашивать смысла нет,
      // но UI должен уметь показать «Текущий ключ: ••••••».
      return v as T;
    },
    [raw],
  );

  const has = useCallback((field: FieldDef) => raw.has(field.key), [raw]);
  const isMasked = useCallback(
    (field: FieldDef) => !!field.secret && looksMasked(raw.get(field.key)),
    [raw],
  );

  const saveSection = useCallback<SettingsState['saveSection']>(
    async (section, entries) => {
      try {
        // Фильтруем замаскированные секреты на клиенте, чтобы не тратить запрос
        // и чтобы поведение не зависело от бэка — даже если он изменит формат маски.
        const safeEntries = entries.filter((e) => !looksMasked(e.value));
        if (safeEntries.length === 0) {
          // Нечего отправлять — считаем, что ок (пользователь «сохранил» без изменений).
          return { ok: true, writtenKeys: [] };
        }
        const res = await bulkUpsertSettings(section, safeEntries);
        // Перезаливаем state — так значения из БД (и маска у секретов) подтянутся.
        await load();
        return { ok: true, writtenKeys: res.writtenKeys };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : 'Не удалось сохранить',
          writtenKeys: [],
        };
      }
    },
    [load],
  );

  return {
    loading,
    error,
    version,
    raw,
    meta,
    get,
    has,
    isMasked,
    reload: load,
    saveSection,
  };
}
